import { type EntryPoint, type EntryPointComponent, type PreloadFetchPolicy } from 'react-relay';

import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';

import type { UserProfileCardAction } from '../types';
import {
	buildQueryVariables,
	type UserProfileCardRelayAgentProps,
} from '../ui/UserProfileCardRelay';

import parameters, {
	type UserProfileCardEntryPointQuery,
	type UserProfileCardEntryPointQuery$variables,
} from './__generated__/UserProfileCardEntryPointQuery.graphql';

const fetchPolicy: PreloadFetchPolicy = 'store-or-network';

/**
 * Inputs the trigger and entry-point need to fetch a user's profile card.
 */
export type UserProfileCardRelayTriggerParams = {
	/** Account ID of the user whose card is being shown. */
	userId: string;
	/** Site ID of the tenant the viewer is currently in. Omit to skip the knowledge-discovery section. */
	cloudId?: string;
	/** Organisation ID. Omit to skip the knowledge-discovery section. */
	orgId?: string;
	/** Account ID of the user viewing the card. Omit to skip the knowledge-discovery section. */
	viewerId?: string;
};

const entryPoint: EntryPoint<
	EntryPointComponent<
		{
			queryRef: UserProfileCardEntryPointQuery;
		},
		Record<string, never>,
		{
			cloudId: string;
			testId?: string;
			agent?: UserProfileCardRelayAgentProps;
			actions?: UserProfileCardAction[];
			viewerId?: string;
		},
		Record<string, never>
	>,
	UserProfileCardRelayTriggerParams
> = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "@atlaskit-internal_user-profile-card-entrypoint" */ './UserProfileCardEntryPoint'
			),
		{
			moduleId: 'user-profile-card-entrypoint',
		},
	),
	getPreloadProps: ({ userId, cloudId, orgId, viewerId }: UserProfileCardRelayTriggerParams) => {
		const variables: UserProfileCardEntryPointQuery$variables = buildQueryVariables({
			userId,
			cloudId,
			orgId,
			viewerId,
		});
		return {
			queries: {
				queryRef: {
					parameters,
					variables,
					options: {
						fetchPolicy,
					},
				},
			},
		};
	},
});

export { entryPoint };
